import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { formatNumberItalian } from '../../utils/numberFormat';

export interface TreeChartNode {
  id: string;
  name: string;
  value: number;
  unit: string;
  percent?: number;
  color?: string;
  children?: TreeChartNode[];
}

export interface TreeChartWrapperProps {
  data: TreeChartNode;
  width?: number;
  height?: number;
  exportFileName?: string;
  showExport?: boolean; // Deprecated, mantiene compatibilità ma non viene più usato
}

interface TreeNodeLayout {
  node: TreeChartNode;
  x: number;
  y: number;
  width: number;
  height: number;
  subtreeWidth: number;
  children?: TreeNodeLayout[];
}

const BOX_WIDTH = 200;
const BOX_HEIGHT = 90;
const HORIZONTAL_SPACING = 100; // gap between columns (aree funzionali)
const VERTICAL_SPACING = 120; // gap between levels
const CHILD_VERTICAL_SPACING = 110; // gap between children in same column (usi energetici)

const formatNumber = (value: number) =>
  formatNumberItalian(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });


// Calculate layout: root -> horizontal columns (aree funzionali) -> vertical children (usi energetici)
const layoutNode = (
  node: TreeChartNode,
  x: number,
  y: number,
  level: number = 0
): TreeNodeLayout => {
  const layout: TreeNodeLayout = {
    node,
    x,
    y,
    width: BOX_WIDTH,
    height: BOX_HEIGHT,
    subtreeWidth: BOX_WIDTH,
  };

  if (node.children && node.children.length > 0) {
    const childrenLayouts: TreeNodeLayout[] = [];

    if (level === 0) {
      // Root level: arrange children horizontally (aree funzionali in tre colonne)
      const totalWidth =
        BOX_WIDTH * node.children.length + HORIZONTAL_SPACING * (node.children.length - 1);
      const startX = x + (BOX_WIDTH - totalWidth) / 2;
      const childY = y + BOX_HEIGHT + VERTICAL_SPACING;

      node.children.forEach((child, idx) => {
        const childX = startX + idx * (BOX_WIDTH + HORIZONTAL_SPACING);
        const childLayout = layoutNode(child, childX, childY, level + 1);
        childrenLayouts.push(childLayout);
      });
    } else {
      // Level 1+ (aree funzionali): arrange children vertically (usi energetici)
      let currentY = y + BOX_HEIGHT + CHILD_VERTICAL_SPACING;

      node.children.forEach(child => {
        const childLayout = layoutNode(child, x, currentY, level + 1);
        childrenLayouts.push(childLayout);
        currentY = childLayout.y + childLayout.height + CHILD_VERTICAL_SPACING;
      });
    }

    layout.children = childrenLayouts;
  }

  return layout;
};

export const TreeChartWrapper: React.FC<TreeChartWrapperProps> = ({
  data,
  width = 1000,
  height = 850,
  exportFileName = 'tree.svg',
  showExport = true,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [translate, setTranslate] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const [menuPos, setMenuPos] = useState<{ mouseX: number; mouseY: number } | null>(null);

  useEffect(() => {
    // Center the root node horizontally
    setTranslate({ x: width / 2 - BOX_WIDTH / 2, y: 50 });
  }, [width]);

  const treeLayout = useMemo(() => layoutNode(data, 0, 0), [data]);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setMenuPos({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleClose = () => setMenuPos(null);

  // Funzione comune per ottenere l'SVG clonato con gli stili applicati
  const getSvg = (): SVGSVGElement | null => {
    const svg = svgRef.current;
    if (!svg) {
      console.error('SVG non trovato');
      return null;
    }

    // Clona l'SVG per non modificare il DOM originale
    const svgClone = svg.cloneNode(true) as SVGSVGElement;

    // Raccoglie tutti i CSS attivi nel documento
    let css = '';
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) {
          css += rule.cssText;
        }
      } catch (e) {
        // cross-origin sheet, ignora
      }
    }

    // Applica CSS globali
    css += `
      text { fill: #000 !important; font-family: Inter, Arial, sans-serif; }
      .link { stroke: #3f3f3f; stroke-width: 1.5px; }
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = css;
    svgClone.insertBefore(styleElement, svgClone.firstChild);

    return svgClone;
  };

  const handleExportSvg = () => {
    const svgClone = getSvg();
    if (!svgClone) return;

    // Serializza e scarica
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = exportFileName.replace('.svg', '').replace('.png', '') + '.svg';
    link.click();
    URL.revokeObjectURL(link.href);
    handleClose();
  };

  const handleExportPng = () => {
    const svgClone = getSvg();
    if (!svgClone) return;

    // Serializza SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // Crea un'immagine dall'SVG
    const img = new Image();
    img.onload = () => {
      // Crea un canvas e disegna l'immagine
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return console.error('Impossibile creare il contesto del canvas');
      }

      // Sfondo bianco
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Disegna l'immagine
      ctx.drawImage(img, 0, 0);

      // Converti in PNG e scarica
      canvas.toBlob(blob => {
        if (!blob) {
          URL.revokeObjectURL(url);
          return console.error('Impossibile creare il blob PNG');
        }
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = exportFileName.replace('.svg', '').replace('.png', '') + '.png';
        link.click();
        URL.revokeObjectURL(link.href);
        URL.revokeObjectURL(url);
        handleClose();
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      console.error("Errore nel caricamento dell'immagine SVG");
    };
    img.src = url;
  };

  const renderNode = (layout: TreeNodeLayout): React.ReactNode => {
    const { node, x, y } = layout;
    const color = node.color || '#e6e6e6';
    const boxX = x;
    const boxY = y;

    return (
      <g key={node.id}>
        {/* Background rectangle */}
        <rect
          x={boxX}
          y={boxY}
          width={BOX_WIDTH}
          height={BOX_HEIGHT}
          fill={color}
          stroke="#3f3f3f"
          strokeWidth={1}
          rx={6}
        />
        {/* Name text */}
        <text
          x={boxX + BOX_WIDTH / 2}
          y={boxY + 30}
          textAnchor="middle"
          fill="#1f1f1f"
          fontSize={13}
          fontWeight="700"
          fontFamily="Inter, Arial, sans-serif"
        >
          {node.name}
        </text>
        {/* Value text */}
        <text
          x={boxX + BOX_WIDTH / 2}
          y={boxY + 50}
          textAnchor="middle"
          fill="#1f1f1f"
          fontSize={12}
          fontFamily="Inter, Arial, sans-serif"
        >
          {formatNumber(node.value)} {node.unit}
        </text>
        {/* Percentage text */}
        {node.percent !== undefined && (
          <text
            x={boxX + BOX_WIDTH / 2}
            y={boxY + 70}
            textAnchor="middle"
            fill="#1f1f1f"
            fontSize={12}
            fontWeight="600"
            fontFamily="Inter, Arial, sans-serif"
          >
            {node.percent.toFixed(1)}%
          </text>
        )}
        {/* Render children */}
        {layout.children &&
          layout.children.length > 0 &&
          (() => {
            const parentCenterX = boxX + BOX_WIDTH / 2;
            const parentBottomY = boxY + BOX_HEIGHT;
            const childCenters = layout.children.map(c => c.x + BOX_WIDTH / 2);
            const childTopYs = layout.children.map(c => c.y);
            const childLeftEdges = layout.children.map(c => c.x);

            // Check if this is a horizontal layout (children have different X positions)
            // or vertical layout (children have same X position, stacked vertically)
            const firstChildX = layout.children[0]?.x || 0;
            const allSameX = layout.children.every(c => Math.abs(c.x - firstChildX) < 1);
            const isVerticalLayout = allSameX && layout.children.length > 0;

            if (isVerticalLayout) {
              // Vertical layout (area funzionale -> usi energetici): L-shaped connectors on the left
              const connectorX = boxX - 30; // Position to the left of the parent node
              const firstChildTopY = Math.min(...childTopYs);
              const lastChildTopY = Math.max(...childTopYs);
              const lastChildCenterY = lastChildTopY + BOX_HEIGHT / 2;

              return (
                <>
                  {/* Vertical line down from parent */}
                  <line
                    x1={parentCenterX}
                    y1={parentBottomY}
                    x2={parentCenterX}
                    y2={parentBottomY + 20}
                    stroke="#3f3f3f"
                    strokeWidth={1.5}
                  />
                  {/* Horizontal line to the left */}
                  <line
                    x1={parentCenterX}
                    y1={parentBottomY + 20}
                    x2={connectorX}
                    y2={parentBottomY + 20}
                    stroke="#3f3f3f"
                    strokeWidth={1.5}
                  />
                  {/* Vertical line on the left, spanning from first to last child center */}
                  <line
                    x1={connectorX}
                    y1={parentBottomY + 20}
                    x2={connectorX}
                    y2={lastChildCenterY}
                    stroke="#3f3f3f"
                    strokeWidth={1.5}
                  />

                  {/* Branches to each child */}
                  {layout.children.map((childLayout, idx) => {
                    const childLeftX = childLeftEdges[idx];
                    const childTopY = childTopYs[idx];
                    const childCenterY = childTopY + BOX_HEIGHT / 2;
                    return (
                      <g key={childLayout.node.id}>
                        {/* Horizontal line from left connector to child - stops at left edge */}
                        <line
                          x1={connectorX}
                          y1={childCenterY}
                          x2={childLeftX}
                          y2={childCenterY}
                          stroke="#3f3f3f"
                          strokeWidth={1.5}
                        />
                        {renderNode(childLayout)}
                      </g>
                    );
                  })}
                </>
              );
            } else {
              // Horizontal layout (root -> aree funzionali): T-shaped connector
              const hubY = parentBottomY + VERTICAL_SPACING / 2;
              const minChildCenterX = Math.min(...childCenters);
              const maxChildCenterX = Math.max(...childCenters);

              return (
                <>
                  {/* Vertical line down from parent */}
                  <line
                    x1={parentCenterX}
                    y1={parentBottomY}
                    x2={parentCenterX}
                    y2={hubY}
                    stroke="#3f3f3f"
                    strokeWidth={1.5}
                  />
                  {/* Horizontal line connecting all children - only between first and last */}
                  {layout.children.length > 1 && (
                    <line
                      x1={minChildCenterX}
                      y1={hubY}
                      x2={maxChildCenterX}
                      y2={hubY}
                      stroke="#3f3f3f"
                      strokeWidth={1.5}
                    />
                  )}

                  {/* Branches to each child */}
                  {layout.children.map((childLayout, idx) => {
                    const childCenterX = childCenters[idx];
                    const childTopY = childTopYs[idx];
                    return (
                      <g key={childLayout.node.id}>
                        {/* Vertical line from horizontal connector to child */}
                        <line
                          x1={childCenterX}
                          y1={hubY}
                          x2={childCenterX}
                          y2={childTopY}
                          stroke="#3f3f3f"
                          strokeWidth={1.5}
                        />
                        {renderNode(childLayout)}
                      </g>
                    );
                  })}
                </>
              );
            }
          })()}
      </g>
    );
  };

  // Calculate total dimensions needed
  const calculateDimensions = (layout: TreeNodeLayout): { width: number; height: number } => {
    let maxX = layout.x + layout.width;
    let maxY = layout.y + layout.height;

    const traverse = (l: TreeNodeLayout) => {
      if (l.children) {
        l.children.forEach(child => {
          maxX = Math.max(maxX, child.x + child.width);
          maxY = Math.max(maxY, child.y + child.height);
          traverse(child);
        });
      }
    };

    traverse(layout);
    return { width: maxX + 50, height: maxY + 100 };
  };

  const dimensions = useMemo(() => calculateDimensions(treeLayout), [treeLayout]);

  return (
    <Box
      position="relative"
      width={width}
      height={height}
      sx={{ overflowY: 'auto', overflowX: 'hidden' }}
      onContextMenu={handleContextMenu}
    >
      <svg
        ref={svgRef}
        width={Math.max(width, dimensions.width)}
        height={Math.max(height, dimensions.height)}
        style={{ display: 'block' }}
      >
        <g transform={`translate(${translate.x}, ${translate.y})`}>{renderNode(treeLayout)}</g>
      </svg>

      <Menu
        open={menuPos !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={menuPos ? { top: menuPos.mouseY, left: menuPos.mouseX } : undefined}
      >
        <MenuItem onClick={handleExportSvg}>Scarica SVG</MenuItem>
        <MenuItem onClick={handleExportPng}>Scarica PNG</MenuItem>
      </Menu>
    </Box>
  );
};
